/**
 * Démarque automatique.
 *
 * Règle boutique : **un mois plein au prix d'origine**, puis −20 points chaque
 * mois suivant, jusqu'au plancher de −80 %. L'article tient encore un mois à
 * ce plancher, après quoi il passe « à récupérer » : il est rendu au déposant
 * ou soldé, mais il ne descend plus. Jamais d'article à 0 €.
 *
 * | Mois révolus depuis le dépôt | Décote | 100 € devient |
 * |------|--------|---------------|
 * | 0    | 0 %    | 100 €         |
 * | 1    | −20 %  | 80 €          |
 * | 2    | −40 %  | 60 €          |
 * | 3    | −60 %  | 40 €          |
 * | 4    | −80 %  | 20 € (plancher) |
 * | 5    | −80 %  | 20 €          |
 *
 * Les constantes ci-dessous sont les seuls réglages : changer la durée du
 * contrat ou les paliers se fait ici et nulle part ailleurs.
 */
/** Nombre de mois pleins avant la première démarque. */
export const MOIS_PLEIN_TARIF = 1;
/** Les décotes successives, un cran de 20 points par mois à partir de `MOIS_PLEIN_TARIF`. */
export const DECOTES_MENSUELLES = [0.2, 0.4, 0.6, 0.8];
/** Décote maximale — plancher de prix. */
export const DECOTE_MAX = DECOTES_MENSUELLES[DECOTES_MENSUELLES.length - 1];
/** Nombre de mois de démarque avant d'atteindre le plancher (5 : −10 → −80 %). */
export const PALIERS_DEMARQUE = DECOTES_MENSUELLES.length;
/**
 * Rang de mois à partir duquel l'article doit être récupéré ou soldé.
 *
 * Le `+ 1` est le mois passé au plancher : l'article ne descend plus, mais il
 * reste en vente une dernière fois avant d'être réclamé.
 */
export const MOIS_FIN_CONTRAT = MOIS_PLEIN_TARIF + PALIERS_DEMARQUE + 1;
/**
 * Pas des décotes proposées à la main, au comptoir. Il est plus fin que le pas
 * mensuel : la démarque automatique avance par crans d'un mois, mais un vendeur
 * doit pouvoir se placer entre deux crans pour conclure une vente.
 */
export const PAS_DECOTE_MANUELLE = 0.1;
/** Les décotes proposées en caisse : plein tarif, −10 %, −20 %… jusqu'au plancher. */
export function decotesProposees() {
    const niveaux = [];
    for (let d = 0; d <= DECOTE_MAX + 1e-9; d += PAS_DECOTE_MANUELLE) {
        niveaux.push(Math.round(d * 100) / 100);
    }
    return niveaux;
}
/**
 * Nombre de mois pleins écoulés depuis le dépôt.
 * 0 pendant le premier mois de contrat, 1 le mois suivant, etc.
 */
export function moisEcoules(deposeLe, maintenant = new Date()) {
    const depot = typeof deposeLe === 'string' ? new Date(deposeLe) : deposeLe;
    if (Number.isNaN(depot.getTime()))
        return 0;
    let mois = (maintenant.getFullYear() - depot.getFullYear()) * 12 +
        (maintenant.getMonth() - depot.getMonth());
    // Le mois n'est complet que passé le quantième du dépôt.
    if (maintenant.getDate() < depot.getDate())
        mois -= 1;
    return Math.max(0, mois);
}
/** Décote en fraction (0.4 = −40 %) au rang de mois donné. */
export function decote(mois) {
    const rang = mois - MOIS_PLEIN_TARIF;
    if (rang < 0)
        return 0;
    return DECOTES_MENSUELLES[Math.min(rang, DECOTES_MENSUELLES.length - 1)];
}
/**
 * Prix affiché en rayon au rang de mois donné, arrondi à l'euro.
 *
 * Le plancher de −80 % ne suffit pas à tenir la promesse « jamais d'article à
 * 0 € » : l'arrondi à l'euro s'en charge tout seul dès que la pièce est bon
 * marché. Un DVD à 2 € démarqué de 80 % vaut 40 centimes, qui s'arrondissent à
 * zéro. Sur le rayon repris de l'ancien logiciel, 13 176 articles sur 72 864
 * étaient dans ce cas — et un article à 0 € s'encaisse sans rien rapporter ni
 * au magasin ni au déposant.
 *
 * D'où le plancher à un euro. Et le garde-fou inverse : si la pièce valait
 * moins d'un euro au départ, la démarque ne doit pas la faire *monter*.
 */
export function prixDemarque(prixInitialCentimes, mois) {
    return prixApresDecote(prixInitialCentimes, decote(mois));
}
/**
 * Prix affiché après une décote quelconque, arrondi à l'euro et jamais nul.
 *
 * Le plancher vaut pour **toute** baisse, pas seulement pour celle du calendrier :
 * une remise accordée d'un doigt au comptoir tombe sur les mêmes pièces bon
 * marché, et le −80 % que la caisse propose sur un livre à 1 € donnait 0,00 €.
 * L'article devenait alors invendable — la caisse refuse un prix nul — et il
 * restait à zéro en rayon jusqu'à ce que quelqu'un pense à retirer la remise.
 * 15 334 pièces du rayon étaient dans ce cas.
 */
export function prixApresDecote(prixInitialCentimes, decoteAppliquee) {
    const arrondi = Math.round((prixInitialCentimes * (1 - decoteAppliquee)) / 100) * 100;
    return Math.min(prixInitialCentimes, Math.max(100, arrondi));
}
export function tableauDemarque(prixInitialCentimes) {
    const lignes = [];
    for (let mois = 0; mois <= MOIS_FIN_CONTRAT - 1; mois += 1) {
        const d = decote(mois);
        lignes.push({
            mois: mois + 1,
            libelle: 
            // Les deux premiers mois sont à plein tarif : les afficher « −0 % »
            // donnerait au déposant l'impression d'une démarque déjà entamée.
            d === 0
                ? `Mois ${mois + 1} — prix d’origine`
                : d >= DECOTE_MAX
                    ? `Mois ${mois + 1} — −${Math.round(d * 100)} % · plancher`
                    : `Mois ${mois + 1} — −${Math.round(d * 100)} %`,
            decote: d,
            prixCentimes: prixDemarque(prixInitialCentimes, mois),
        });
    }
    return lignes;
}
export const LIBELLE_STATUT = {
    en_vente: 'En vente',
    demarque: 'Démarqué',
    a_recuperer: 'À récupérer',
    vendu: 'Vendu',
    rendu: 'Rendu au client',
    donne: 'Donné',
    retire: 'Retiré',
};
/** L'article a-t-il définitivement quitté le rayon ? */
export function estSorti(statut) {
    return statut === 'vendu' || statut === 'rendu' || statut === 'donne' || statut === 'retire';
}
/**
 * Statut vivant d'un article encore en rayon, déduit de son ancienneté.
 * Les statuts définitifs (vendu, retiré) sont stockés en base et priment.
 *
 * Un article dont la dévaluation progressive est décochée ne passe jamais
 * « Démarqué » — son prix ne bouge pas — mais le contrat s'achève quand même.
 */
export function statutSelonAnciennete(mois, demarqueActive = true) {
    if (mois >= MOIS_FIN_CONTRAT)
        return 'a_recuperer';
    if (demarqueActive && mois >= MOIS_PLEIN_TARIF)
        return 'demarque';
    return 'en_vente';
}
/** « −40 % · mois 3 » ou « — » le premier mois. */
export function libelleDemarque(mois) {
    const d = decote(mois);
    return d === 0 ? '—' : `−${Math.round(d * 100)} % · mois ${mois + 1}`;
}
//# sourceMappingURL=demarque.js.map